/**
 * 日付・日時のフォーマット (JST = Asia/Tokyo 固定)。
 *
 * API は ISO 8601 UTC を返す (例: "2026-05-26T05:30:00.000Z")。生のまま
 * `.slice(0, 10)` / `.replace('T', ' ')` すると UTC のまま表示されてしまい
 * JST と 9 時間ズレる。表示は常に JST に変換する。
 *
 * ロケールは `en-CA` を採用 (年-月-日 のゼロ埋め + 24h 表記が固定で得られる
 * ため、ja-JP / en-US の swing を回避)。
 */

const ZONE = 'Asia/Tokyo'

const dateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

const dateTimeFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

const yearFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: ZONE,
  year: 'numeric',
})

const pick = (
  parts: readonly Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes,
): string => parts.find(p => p.type === type)?.value ?? ''

/** 著作権表記の開始年 (Gomogy Co., Ltd. のサービス開始年)。 */
export const COPYRIGHT_START_YEAR = 2026

/**
 * 著作権表記の年範囲を返す (JST 基準)。開始年のみなら `"2026"`、現在年が開始年より
 * 後なら `"2026-<現在年>"`。i18n の `{years}` に渡す。年は実行時に算出するため、
 * 年が変わっても表記は自動更新される。
 */
export const copyrightYears = (now: Date = new Date()): string => {
  const current = Number(yearFormatter.format(now))

  return current > COPYRIGHT_START_YEAR
    ? `${COPYRIGHT_START_YEAR}-${current}`
    : `${COPYRIGHT_START_YEAR}`
}

const parseIso = (iso: string): Date | null => {
  const d = new Date(iso)

  return Number.isNaN(d.getTime()) ? null : d
}

/** ISO 8601 UTC を JST `YYYY-MM-DD` に整形。invalid なら入力をそのまま返す。 */
export const formatDateJst = (iso: string): string => {
  const d = parseIso(iso)
  if (!d) return iso

  return dateFormatter.format(d)
}

/** ISO 8601 UTC を JST `YYYY-MM-DD HH:mm` (24h) に整形。invalid なら入力をそのまま返す。 */
export const formatDateTimeJst = (iso: string): string => {
  const d = parseIso(iso)
  if (!d) return iso

  const parts = dateTimeFormatter.formatToParts(d)
  // en-CA の 24h 表記で `24:00` が出るケース (00:00 を 24:00 と表現する古い ICU) を
  // 念のため `00` に正規化する。
  const hour = pick(parts, 'hour') === '24' ? '00' : pick(parts, 'hour')

  return `${pick(parts, 'year')}-${pick(parts, 'month')}-${pick(parts, 'day')} ${hour}:${pick(parts, 'minute')}`
}
